import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { CustomerFormModal } from './CustomerFormModal';
import { listCustomers, type CustomerDto } from './api';

type Scope = 'active' | 'all';

/**
 * Customer master — who orders goes out to.
 *
 * There is no warehouse filter as there is on suppliers. The list is already scoped to the
 * warehouse the session is working in, plus customers assigned nowhere, so a filter here would
 * offer a choice the server has already made.
 */
export function CustomersRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<CustomerDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [search, setSearch] = useState('');

  const canWrite = permissions.has('customers:write');

  // Active is applied server-side; the search runs over the fetched slice, which is fine at
  // customer scale — this is hundreds of rows, not the hundreds of thousands locations can be.
  const query = useQuery({
    queryKey: ['customers', scope] as const,
    queryFn: ({ signal }) => listCustomers({ includeInactive: scope === 'all' }, signal),
  });

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (c) =>
        c.customerName.toLowerCase().includes(needle) ||
        c.customerNumber.toLowerCase().includes(needle) ||
        (c.companyName?.toLowerCase().includes(needle) ?? false) ||
        (c.contactName?.toLowerCase().includes(needle) ?? false),
    );
  }, [query.data, search]);

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(customer: CustomerDto) {
    setEditing(customer);
    open();
  }

  function onSaved() {
    // Both slices: a customer deactivated in the modal leaves the active list, and one reactivated
    // joins it.
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
  }

  /**
   * The name is the headline rather than the number: people say "Acme", and the number is what
   * they look up afterwards.
   */
  const columns: DataColumn<CustomerDto>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('customers.name'),
        card: 'title',
        render: (c) => c.customerName,
      },
      {
        key: 'number',
        label: t('customers.number'),
        card: 'subtitle',
        render: (c) => <Code>{c.customerNumber}</Code>,
      },
      {
        key: 'company',
        label: t('customers.companyName'),
        render: (c) => <Text size="sm">{c.companyName ?? '—'}</Text>,
      },
      {
        key: 'contact',
        label: t('customers.contact'),
        render: (c) => (
          <Stack gap={0}>
            <Text size="sm">{c.contactName ?? '—'}</Text>
            {c.phone && (
              <Text size="xs" c="dimmed">
                {c.phone}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'email',
        label: t('customers.email'),
        render: (c) => <Text size="sm">{c.email ?? '—'}</Text>,
      },
      {
        key: 'status',
        label: t('customers.status'),
        card: 'aside',
        render: (c) =>
          c.isActive ? (
            <Text size="sm" c="dimmed">
              {t('customers.activeState')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('customers.inactiveState')}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.customers')}
        actions={
          canWrite ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('customers.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            label={t('customers.search')}
            placeholder={t('customers.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 280 }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('customers.show')}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { label: t('customers.activeOnly'), value: 'active' },
                { label: t('customers.all'), value: 'all' },
              ]}
            />
          </Stack>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('customers.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('customers.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim() ? t('customers.noMatch') : t('customers.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(c) => c.customerId}
            minWidth={820}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={
              canWrite
                ? (c) => (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('customers.edit', { name: c.customerName })}
                      onClick={() => startEdit(c)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <CustomerFormModal opened={modalOpen} onClose={close} customer={editing} onSaved={onSaved} />
    </>
  );
}
